import { isAxiosError } from 'axios'
import axios from '@/shared/api/axios'
import { API } from '@/shared/constants/api'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import type {
  IUserAuth,
  IUserCredentials,
  IUserForgotPassword,
  IUserResetPassword
} from '../users/models/user'

export const axiosErrorHandler = (e: any) => {
  if (isAxiosError(e) && e.response) {
    return e.response
  }
  return e
}

export interface IResponseUser {
  user: IUserAuth | null
  authTwoFactor?: boolean
  status: number
  error?: string
}

export const signIn = async (credentials: IUserCredentials): Promise<IResponseUser> => {
  try {
    const response = await axios.post(API.auth.login, JSON.stringify(credentials), {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true
    })

    if (response?.data?.accessToken) {
      return {
        user: response?.data,
        status: response?.status
      }
    }

    return {
      user: null,
      authTwoFactor: true,
      status: response?.status
    }
  } catch (e) {
    console.error('ERROR::SIGN_IN => ', e)

    // Route through the central resolver: it translates the errorCode, falls back to the
    // server's human message, then to a network/generic message — so `error` is NEVER
    // undefined (which produced an empty, ambiguous toast on login).
    return {
      user: null,
      status: isAxiosError(e) ? (e.response?.status ?? 500) : 500,
      error: getErrorMessage(e)
    }
  }
}

export const forgotPassword = async (credentials: IUserForgotPassword) => {
  try {
    const response = await axios.post(API.auth.forgotPassword, JSON.stringify(credentials), {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    return response
  } catch (e) {
    console.error('ERROR::FORGOT_PASSWORD => ', e)
    return axiosErrorHandler(e)
  }
}

export const verifyTokenResetPassword = async (id: string, token: string) => {
  try {
    const response = await axios.get(`${API.auth.resetPassword}/${id}/${token}`)
    return response
  } catch (e) {
    console.error('ERROR::RESET_PASSWORD_VERIFY_TOKEN => ', e)
    return axiosErrorHandler(e)
  }
}

export const resetPassword = async (id: string, token: string, credentials: IUserResetPassword) => {
  try {
    const response = await axios.post(
      `${API.auth.resetPassword}/${id}/${token}`,
      JSON.stringify(credentials),
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    return response
  } catch (e) {
    console.error('ERROR::RESET_PASSWORD => ', e)
    return axiosErrorHandler(e)
  }
}
